import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../api/axois";
import styles from "./OrderPage.module.css";
import { FaShoppingCart, FaTrash, FaPlus, FaMinus, FaQrcode, FaChevronDown, FaSearch, FaTimes } from "react-icons/fa";
import ChopNationNavbar from "../../components/User/ChopNationNavbar";
import ChopNationFooter from "../../components/User/ChopNationFooter";
import AuthModal from "../../components/User/AuthModal";
import SwallowAddonModal from "../../components/User/SwallowAddonModal";
import { getImageUrl } from "../../utils/imageUtils";

const FALLBACK_ITEM_IMAGE = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c";
// Display-only — the backend computes the authoritative charge (CHOPNATION_SERVICE_CHARGE_RATE).
const SERVICE_CHARGE_RATE = 0.1;

const OrderPage = ({ initialAuthOpen = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuData, setMenuData] = useState([]);
  const [addons, setAddons] = useState([]);
  const [cart, setCart] = useState([]);
  const [activeSwallowItem, setActiveSwallowItem] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState({
    name: "",
    phone: "",
    roomNumber: ""
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(initialAuthOpen);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackSuccess, setFeedbackSuccess] = useState("");

  // Reuses the site's existing /contact endpoint (same one ContactForm.jsx
  // posts to) rather than a new chopnation-specific feedback endpoint —
  // the `source` tag is what distinguishes dining feedback in the inbox.
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setFeedbackLoading(true);
    setFeedbackError("");
    setFeedbackSuccess("");
    try {
      const res = await api.post("/contact", {
        email: feedbackEmail,
        message: feedbackMessage,
        source: "chopnation_feedback"
      });
      setFeedbackSuccess(res.data.message || "Thanks for the feedback!");
      setFeedbackEmail("");
      setFeedbackMessage("");
    } catch (err) {
      setFeedbackError(err.response?.data?.message || "Failed to send feedback. Please try again.");
    } finally {
      setFeedbackLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setCollapsedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const categoriesWithItems = menuData.filter(category => category.menu_items?.length > 0);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const visibleCategories = categoriesWithItems
    .filter(category => activeCategory === "all" || String(category.id) === String(activeCategory))
    .map(category => ({
      ...category,
      menu_items: normalizedQuery
        ? category.menu_items.filter(item =>
            item.name.toLowerCase().includes(normalizedQuery) ||
            (item.description || "").toLowerCase().includes(normalizedQuery)
          )
        : category.menu_items
    }))
    .filter(category => category.menu_items.length > 0);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await api.get("/chopnation/menu");
        setMenuData(response.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to load menu items.");
        setLoading(false);
      }
    };
    fetchMenu();

    const fetchAddons = async () => {
      try {
        const response = await api.get("/chopnation/addons");
        setAddons(response.data);
      } catch {
        // Swallow items just won't be selectable if this fails silently;
        // the rest of the menu still works without add-ons.
      }
    };
    fetchAddons();
  }, []);

  // Pre-fill the room number from a query param if one was provided (e.g. ?room=101)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const room = params.get("room");
    if (room) {
      setDeliveryInfo(prev => ({ ...prev, roomNumber: room }));
    }
  }, [location.search]);

  // Plain items (no add-ons) merge into an existing cart line by menu item id.
  // Customized swallow lines get their own unique cartId so two different
  // soup/add-on combos of the same base item can coexist in the cart.
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(i => i.cartId === item.id);
      if (existing) {
        return prev.map(i => i.cartId === item.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, cartId: item.id, quantity: 1 }];
    });
  };

  const addCustomizedToCart = (entry) => {
    const cartId = `${entry.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setCart(prev => [...prev, { ...entry, cartId }]);
  };

  const openSwallowModal = (item) => setActiveSwallowItem(item);

  const removeFromCart = (cartId) => {
    setCart(prev => prev.filter(i => i.cartId !== cartId));
  };

  const updateQuantity = (cartId, delta) => {
    setCart(prev => prev.map(i => {
      if (i.cartId === cartId) {
        const newQty = Math.max(1, i.quantity + delta);
        return { ...i, quantity: newQty };
      }
      return i;
    }));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateServiceCharge = () => {
    return Math.round(calculateSubtotal() * SERVICE_CHARGE_RATE * 100) / 100;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateServiceCharge();
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (cart.length === 0) {
      setError("Your cart is empty");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const basePayload = {
        items: cart.map(item => ({
          id: item.id,
          quantity: item.quantity,
          ...(item.addons?.length
            ? { addons: item.addons.map(a => ({ addon_id: a.addon_id })) }
            : {})
        })),
        order_type: 'delivery',
        location: `Room ${deliveryInfo.roomNumber}`,
        room_number: deliveryInfo.roomNumber,
        payment_method: 'paystack'
      };

      // Guests provide a name and phone number — the backend fills in the
      // email for orders without an authenticated ChopNation user.
      const orderPayload = {
        ...basePayload,
        customer_name: deliveryInfo.name,
        customer_phone: deliveryInfo.phone
      };

      const response = await api.post("/chopnation/orders", orderPayload);

      // If Paystack is used, the backend returns an authorization URL
      if (response.data.authorization_url) {
        // Redirect the user to Paystack for payment completion
        window.location.href = response.data.authorization_url;
        return; // Stop further processing until payment completes
      }
      
      alert("Order placed successfully!");
      setCart([]);
      setShowCheckout(false);
      navigate("/profile");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.mainWrapper}>
      <ChopNationNavbar 
        cartCount={cart.reduce((sum, item) => sum + item.quantity, 0)} 
        onOpenAuth={() => setIsAuthModalOpen(true)}
      />
      
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>AURELIA <span>DINING</span></h1>
          <p className={styles.subtitle}>Your Premium Room Service & Dining Experience</p>
        </header>

        <div className={styles.layout} id="menu">
          <div className={styles.menuSection}>
            <div className={styles.menuControls}>
              <div className={styles.searchBar}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search dishes..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className={styles.searchInput}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className={styles.searchClear}
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              <div className={styles.categoryTabs}>
                <button
                  type="button"
                  className={activeCategory === "all" ? `${styles.categoryTab} ${styles.categoryTabActive}` : styles.categoryTab}
                  onClick={() => setActiveCategory("all")}
                >
                  All
                </button>
                {categoriesWithItems.map(category => (
                  <button
                    key={category.id}
                    type="button"
                    className={String(activeCategory) === String(category.id) ? `${styles.categoryTab} ${styles.categoryTabActive}` : styles.categoryTab}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <p>Loading gourmet menu...</p>
            ) : menuData.length === 0 ? (
              <p>No menu items available currently.</p>
            ) : visibleCategories.length === 0 ? (
              <p>No dishes match your search.</p>
            ) : visibleCategories.map((category) => {
              // While actively searching, force every matching category open —
              // a match hidden behind a previously-collapsed section would be
              // confusing to a user who just typed to find it.
              const isCollapsed = !normalizedQuery && !!collapsedCategories[category.id];
              return (
                <div key={category.id} className={styles.category}>
                  <button
                    type="button"
                    className={styles.categoryTitle}
                    onClick={() => toggleCategory(category.id)}
                    aria-expanded={!isCollapsed}
                  >
                    {category.name.toUpperCase()}
                    <FaChevronDown className={`${styles.categoryChevron} ${isCollapsed ? styles.categoryChevronCollapsed : ""}`} />
                  </button>
                  <div className={`${styles.categoryBody} ${isCollapsed ? styles.categoryBodyCollapsed : ""}`}>
                    <div className={styles.menuGrid}>
                      {category.menu_items.map(item => (
                        <div key={item.id} className={styles.menuItem}>
                          <div className={styles.itemImageWrapper}>
                            <img src={getImageUrl(item.image, FALLBACK_ITEM_IMAGE)} alt={item.name} className={styles.itemImage} />
                            <span className={`${styles.itemClassBadge} ${styles[`itemClassBadge_${item.item_class}`] || ""}`}>
                              {item.item_class}
                            </span>
                          </div>
                          <div className={styles.itemContent}>
                            <div className={styles.itemInfo}>
                              <h3 className={styles.itemName}>{item.name}</h3>
                              <p className={styles.itemDesc}>{item.description}</p>
                              <p className={styles.itemPrice}>₦{parseFloat(item.base_price).toLocaleString()}</p>
                            </div>
                            <button
                              className={styles.addBtn}
                              onClick={() => item.item_class === "swallow"
                                ? openSwallowModal(item)
                                : addToCart({
                                    id: item.id,
                                    name: item.name,
                                    price: parseFloat(item.base_price)
                                  })
                              }
                            >
                              <FaPlus /> {item.item_class === "swallow" ? "Choose" : "Add"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <aside className={`${styles.cartSection} ${mobileCartOpen ? styles.cartSectionOpen : ""}`}>
            <div className={styles.cartCard}>
              {/* Mobile-only docked bar — the cart's full content lives below
                  and only becomes visible/scrollable when this is tapped open.
                  On desktop this bar is hidden and the card just renders normally. */}
              <button
                type="button"
                className={styles.cartMobileBar}
                onClick={() => setMobileCartOpen(open => !open)}
                aria-expanded={mobileCartOpen}
              >
                <span className={styles.cartMobileBarLeft}>
                  <FaShoppingCart />
                  Your Order
                  {cart.length > 0 && (
                    <span className={styles.cartMobileBadge}>
                      {cart.reduce((sum, item) => sum + item.quantity, 0)}
                    </span>
                  )}
                </span>
                <span className={styles.cartMobileBarRight}>
                  ₦{calculateTotal().toLocaleString()}
                  <FaChevronDown className={`${styles.cartMobileChevron} ${mobileCartOpen ? styles.cartMobileChevronOpen : ""}`} />
                </span>
              </button>

              <div className={styles.cartCardBody}>
                <h2 className={styles.cartTitle}>
                  <FaShoppingCart /> Your Order
                </h2>
                {cart.length === 0 ? (
                  <p className={styles.emptyCart}>Your cart is empty</p>
                ) : (
                  <>
                    <div className={styles.cartItems}>
                      {cart.map(item => (
                        <div key={item.cartId} className={styles.cartItem}>
                          <div className={styles.cartItemInfo}>
                            <span className={styles.cartItemName}>{item.name}</span>
                            {item.addons?.length > 0 && (
                              <span className={styles.cartItemAddons}>
                                {item.addons.map(a => a.name).join(", ")}
                              </span>
                            )}
                            <span className={styles.cartItemPrice}>₦{(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                          <div className={styles.cartItemActions}>
                            <button onClick={() => updateQuantity(item.cartId, -1)}><FaMinus /></button>
                            <span>{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.cartId, 1)}><FaPlus /></button>
                            <button onClick={() => removeFromCart(item.cartId)} className={styles.deleteBtn}><FaTrash /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className={styles.cartSubtotal}>
                      <span>Subtotal</span>
                      <span>₦{calculateSubtotal().toLocaleString()}</span>
                    </div>
                    <div className={styles.cartSubtotal}>
                      <span>Service Charge (10%)</span>
                      <span>₦{calculateServiceCharge().toLocaleString()}</span>
                    </div>
                    <div className={styles.cartTotal}>
                      <span>Total</span>
                      <span>₦{calculateTotal().toLocaleString()}</span>
                    </div>
                    {!showCheckout ? (
                      <button
                        className={styles.checkoutBtn}
                        onClick={() => setShowCheckout(true)}
                      >
                        Proceed to Checkout
                      </button>
                    ) : (
                      <form onSubmit={handleCheckout} className={styles.form}>
                        <input
                          type="text"
                          placeholder="Full Name"
                          required
                          value={deliveryInfo.name}
                          onChange={e => setDeliveryInfo({...deliveryInfo, name: e.target.value})}
                        />
                        <input
                          type="tel"
                          placeholder="Phone Number"
                          required
                          value={deliveryInfo.phone}
                          onChange={e => setDeliveryInfo({...deliveryInfo, phone: e.target.value})}
                        />

                        <input
                          type="text"
                          placeholder="Room Number"
                          required
                          value={deliveryInfo.roomNumber}
                          onChange={e => setDeliveryInfo({...deliveryInfo, roomNumber: e.target.value})}
                        />

                        {error && <p className={styles.error}>{error}</p>}

                        <button
                          type="submit"
                          className={styles.payBtn}
                          disabled={loading}
                        >
                          {loading ? "Processing..." : `Pay ₦${calculateTotal().toLocaleString()}`}
                        </button>
                        <button
                          type="button"
                          className={styles.backBtn}
                          onClick={() => setShowCheckout(false)}
                        >
                          Back to Menu
                        </button>
                      </form>
                    )}
                  </>
                )}
              </div>
            </div>
          </aside>
        </div>

        {/* Story Section */}
        <section id="story" className={styles.infoSection}>
          <div className={styles.sectionHeader}>
            <h2>Our Story</h2>
            <div className={styles.underline}></div>
          </div>
          <div className={styles.storyContent}>
            <div className={styles.storyText}>
              <p>Aurelia Dining was born from a simple idea: that fine dining should be accessible, digital, and effortless for all our guests. We bridge the gap between our gourmet kitchens and your dining table.</p>
              <p>Our chefs use only the freshest local ingredients to craft meals that don't just fill you up, but tell a story of flavor and passion. From our signature breakfast stacks to our stone-baked pizzas, every dish is a masterpiece.</p>
            </div>
            <div className={styles.storyImage}>
              <img src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?q=80&w=800&auto=format&fit=crop" alt="Kitchen" />
            </div>
          </div>
        </section>

        {/* Support Section */}
        <section id="support" className={styles.infoSection}>
          <div className={styles.sectionHeader}>
            <h2>Support & Contact</h2>
            <div className={styles.underline}></div>
          </div>
          <div className={styles.supportGrid}>
            <div className={styles.supportCard}>
              <h3>Ordering Help</h3>
              <p>Need help with your order? Our kitchen staff is standing by.</p>
              <a href="tel:+2348165028212" className={styles.contactBtn}>Call the Kitchen</a>
            </div>
            <div className={styles.supportCard}>
              <h3>Feedback</h3>
              <p>How was your meal? We'd love to hear your thoughts.</p>
              {!showFeedbackForm ? (
                <button type="button" className={styles.contactBtn} onClick={() => setShowFeedbackForm(true)}>
                  Send Feedback
                </button>
              ) : (
                <form className={styles.feedbackForm} onSubmit={handleFeedbackSubmit}>
                  <input
                    type="email"
                    placeholder="Your email"
                    required
                    value={feedbackEmail}
                    onChange={e => setFeedbackEmail(e.target.value)}
                  />
                  <textarea
                    placeholder="Tell us about your meal..."
                    rows="3"
                    required
                    value={feedbackMessage}
                    onChange={e => setFeedbackMessage(e.target.value)}
                  />
                  {feedbackError && <p className={styles.feedbackError}>{feedbackError}</p>}
                  {feedbackSuccess && <p className={styles.feedbackSuccess}>{feedbackSuccess}</p>}
                  <button type="submit" className={styles.contactBtn} disabled={feedbackLoading}>
                    {feedbackLoading ? "Sending..." : "Submit Feedback"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>

      <ChopNationFooter />
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        origin="order"
      />
      <SwallowAddonModal
        isOpen={!!activeSwallowItem}
        item={activeSwallowItem}
        addons={addons}
        onClose={() => setActiveSwallowItem(null)}
        onConfirm={(entry) => {
          addCustomizedToCart(entry);
          setActiveSwallowItem(null);
        }}
      />
    </div>
  );
};

export default OrderPage;
